import { MouseEvent, useCallback, useState } from 'react'

import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'

import { zIndex as zIndexEnum } from '../../styles/zIndex'
import * as S from './ConfirmAction.styles'
import { FloatingConfirmActionProps } from './ConfirmAction.types'
import { ConfirmActionContent } from './ConfirmActionContent'

export const FloatingConfirmAction = ({
  children,
  containerStyle,
  cancelText,
  closeOnScroll = false,
  confirmText,
  confirmButtonType,
  isSubmitButtonDisabled = false,
  message,
  offsetSize = 5,
  onConfirm,
  onClick = () => null,
  onToggleMessage,
  placement = 'bottom-start',
  title,
  theme,
  strategy = 'fixed',
  zIndex = zIndexEnum.GLOBAL_LOADING,
}: FloatingConfirmActionProps) => {
  const [showMessage, setShowMessage] = useState(false)

  const { context, refs, floatingStyles } = useFloating<HTMLElement>({
    placement,
    whileElementsMounted: autoUpdate,
    strategy,
    onOpenChange: setShowMessage,
    open: showMessage,
    middleware: [offset(offsetSize), flip()],
  })

  const dismiss = useDismiss(context, { ancestorScroll: closeOnScroll })

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  const handleHideMessage = useCallback(() => {
    setShowMessage(false)
    if (onToggleMessage) {
      onToggleMessage(false)
    }
  }, [onToggleMessage])

  const handleShowMessage = useCallback(() => {
    setShowMessage(true)
    if (onToggleMessage) {
      onToggleMessage(true)
    }
  }, [onToggleMessage])

  const handleOnActionConfirmed = useCallback(() => {
    onConfirm?.()
    handleHideMessage()
  }, [onConfirm, handleHideMessage])

  const handleOnClickAction = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      onClick(e)
      handleShowMessage()
    },
    [handleShowMessage, onClick],
  )

  return (
    <S.Container style={containerStyle} ref={refs.setReference} {...getReferenceProps()}>
      <S.ChildrenContainer onClick={handleOnClickAction}>{children}</S.ChildrenContainer>
      {showMessage ? (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={{ ...floatingStyles, zIndex }}
              {...getFloatingProps()}
            >
              <ConfirmActionContent
                message={message}
                onCancel={handleHideMessage}
                onConfirm={handleOnActionConfirmed}
                title={title}
                theme={theme}
                cancelText={cancelText}
                confirmText={confirmText}
                confirmButtonType={confirmButtonType}
                isSubmitButtonDisabled={isSubmitButtonDisabled}
              />
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </S.Container>
  )
}
